/*
 * welcome-drawer.js: the welcome drawer (zao#617). Markup: snippets/welcome-drawer.liquid (rendered once from
 * layout/theme.liquid) and the phone menu line in sections/header.liquid. Look: assets/welcome-drawer.css.
 *
 * What it shares with the footer sign-up (assets/footer-signup.js, window.SofsyFooterSignup), so the two never differ:
 *   - assignOffer(): the visitor's one offer, 'gloves' | 'pct10' (the Klaviyo pop-up's arm when known, else the
 *     footer's own 50/50, kept in localStorage), so the drawer and the footer always show her the same offer;
 *   - subscribe(): the bot trap, the 1 s wait from her first touch, ONE request to Klaviyo's client subscription API
 *     (list XdwVYY, double opt-in), then klaviyo.identify and the "has signed up" mark. The drawer's request says
 *     custom_source 'Welcome drawer', signup_source 'drawer_menu' | 'drawer_auto', signup_entry 'drawer_menu' |
 *     'drawer_auto' | 'drawer_exit' | 'drawer_campaign', and the shared where-she-came-from properties;
 *   - isEmail(), and the footer's error words.
 * Without that module (it failed to load) nothing here ever shows: no button that cannot send.
 * The one exception to assignOffer (FORCED OFFER, a separate commit so it can be dropped): an ad may name the offer
 * with wd_offer=gloves | pct10 in the landing address. Only that query parameter, only an exact known offer (anything
 * else is ignored and the A/B stands); it is kept for the visit (sessionStorage FORCED_KEY, known offers only, so a
 * later page in the visit shows it too). The drawer shows it, sends it as signup_offer and adds the Klaviyo profile
 * property signup_offer_forced 'yes'. It never writes the A/B arm (footer-signup.js's sofsy_signup_offer) and does
 * not change the footer's offer. Not on the tracking bus (the receiver accepts only entry and offer on drawer.open).
 *
 * Who never sees it (Ben, 5 Oct: hide only from subscribers and from anyone who has ordered):
 *   - localStorage sofsy_signed_up = '1': set after a real 2xx from the drawer or the footer, and on Klaviyo's submit
 *     event from one of sofsy's two sign-up pop-ups (footer-signup.js; never from another form, back in stock
 *     included);
 *   - a signed-in customer who accepts email marketing or has ordered before (the snippet does not render at all).
 *   Being known to Klaviyo (its __kla_id cookie, klaviyo.isIdentified()) is NOT a reason: known is not subscribed.
 *
 * The ways in (Ben, 6 Oct: no sticky tab; show it to more people):
 *   1. the phone menu line "Welcome gift": closes the menu, then always opens the drawer (she asked);
 *   2. automatic, once per visit (sessionStorage, set only once the drawer is really seen), on these pages only:
 *      - product pages: after 30 s on the page or half the page scrolled; never in the first 15 s; never within 8 s
 *        of a size or Add to Bag tap;
 *      - home, collection, collection list and search pages: after 12 s or 40% scrolled; never in the first 8 s;
 *        never within 8 s of a tap on a product card (its picture, name, colour swatch or quick add);
 *      - never on any other page (cart, checkout, account, policies ...);
 *      - everywhere: never while something else is open (busy(): the bag, the size and quantity drawer, a sheet, the
 *        menu, a Klaviyo pop-up, the Consentmo cookie banner while it shows ...), never within 1 day of her x
 *        (SNOOZE_MS, the same day Ben set on the Klaviyo phone pop-up), never for anyone signed up. A full bag no
 *        longer stops it (Ben, 6 Oct).
 *      On desktop also on exit intent (the pointer leaves through the top of the window) on the same pages and rules.
 *      Sign-up campaign visits (zao#617, Ben 6 Oct: an ad that asks for an email sends her to a product page; 30 s
 *      is too long to wait for the offer): when the landing address carries utm_campaign containing "signup",
 *      "sign_up", "sign-up" or "sign up" (any case, e.g. lg1_signup_gloves) or wd=1, the visit is marked (sessionStorage CAMPAIGN_KEY, so a second page in
 *      the visit gets it too) and the automatic opening on those same pages comes after CAMPAIGN_RULES' 3 s instead,
 *      entry 'campaign'. Only the timing changes: every other rule here (the pages, the cookie banner and busy(),
 *      never over the price or Add to Bag with its wait and yield, the 8 s after a buying tap, the 1-day pause, once
 *      per visit, signed up, the theme editor, the seen check) applies exactly as for 'auto'. On a phone product page
 *      the pinned Add to Bag bar usually shows at first, so there it still waits until it no longer would cover it.
 *      Never where it would cover the product page's price or an Add to Bag (round 1's rule): before an automatic
 *      opening the drawer's final rectangle is measured (laid out hidden, no animation) against every VISIBLE Add to
 *      Bag (the page's button, the pinned bar on phones) and price in the product page's buy area (BUY below; a
 *      collection card's price does not count). If they meet, it waits and checks again on her next scroll or the next
 *      1 s tick. On phones the pinned bar shows whenever the page's own button is below the screen (bundle.js
 *      handleStickyBuyButton), so there it opens once she has scrolled past the buy area.
 *      The check goes on while an automatic opening is open: on her scrolls and on resizes (throttled), if the price
 *      or a visible Add to Bag comes under it, it slides away (drawer.close {how: yield}). That is not her x: no
 *      1-day pause, and no other automatic opening in this visit. One she opened stays put, and so does an automatic
 *      one she has started using (touched the form, a send on its way, the thank-you, focus inside).
 *      Seen, not just opened: about 1 s after an automatic opening, the point at the drawer's centre must belong to
 *      the drawer. Only then are drawer.open sent and the visit marked. If something covers it (a cookie banner
 *      busy() could not see), it slides away again and NOTHING is sent; it may try once more on this page, no
 *      sooner than 10 s later, and after 2 covered openings in a visit it stops for the visit (the menu line still
 *      works). A touch of the form, a send, or her x / Escape also count as seen.
 *      The bag counts as open while it slides out too (BAG_OUT_MS after it starts closing).
 *
 * Events (window.sofsyTrack, assets/sofsy-track.js; never the address or anything she typed):
 *   drawer.open {entry: menu | auto | exit | campaign, offer}
 *   drawer.open is sent when she has seen it: at once for the menu line, after the seen check for an automatic one
 *   drawer.close {how: x | escape | yield}         yield: a seen automatic opening stepped aside for the price or
 *                                                  Add to Bag; a covered, never-seen opening sends nothing at all
 *   drawer.signup.success {offer, entry, lead?}    after a 2xx only (a pretend thank-you for a bot is not counted):
 *                                                  sent by sofsy-track.js's lead decider (SofsyTrack.signupSuccess),
 *                                                  which the shared subscribe() calls; only without that decider
 *                                                  does showThanks send the old {offer, entry} itself
 *   drawer.signup.fail {offer, reason}             invalid_email | other; once per reason per page view
 */
(() => {
  'use strict';

  if (window.SofsyWelcomeDrawer) return;

  /* ---------- the rules, as numbers ---------- */
  const SNOOZE_MS = 24 * 60 * 60 * 1000; // after the x: no automatic opening for 1 day (Klaviyo phone pop-up: 1 day)
  // Automatic opening, per kind of page (Ben, 6 Oct): after `after` ms on the page or `scroll` of it scrolled, never
  // before `notBefore` ms, never within QUIET_AFTER_INTENT_MS of a buying tap.
  const RULES = Object.freeze({
    product: Object.freeze({ after: 30000, scroll: 0.5, notBefore: 15000 }),
    browse: Object.freeze({ after: 12000, scroll: 0.4, notBefore: 8000 }),
  });
  // Templates (template.name) and the rules they get; any other page never opens by itself.
  const PAGE_KINDS = Object.freeze({ product: 'product', index: 'browse', collection: 'browse', 'list-collections': 'browse', search: 'browse' });
  // A sign-up campaign visit (see the top): 3 s on any of those pages, whatever the scroll.
  const CAMPAIGN_RULES = Object.freeze({ after: 3000, scroll: 0, notBefore: 3000 });
  const CAMPAIGN_KEY = 'sofsy_wd_campaign'; // sessionStorage: this visit landed from a sign-up campaign
  const AUTO_AFTER_MS = RULES.product.after; // kept for the tests' names
  const AUTO_NEVER_BEFORE_MS = RULES.product.notBefore;
  const QUIET_AFTER_INTENT_MS = 8000; // never within 8 s of a size, Add to Bag or product-card tap
  const SEEN_CHECK_MS = 1100; // after the 1 s entrance: is it really in view?
  const RETRY_AFTER_MS = 10000; // after a covered (unseen) automatic opening: one retry, no sooner than this
  const MAX_UNSEEN_PAGE = 2; // covered openings per page view (the first plus one retry)
  const MAX_UNSEEN_VISIT = 2; // covered openings per visit; then no more automatic opening this visit
  const UNSEEN_KEY = 'sofsy_wd_unseen'; // sessionStorage: covered openings this visit
  const BAG_OUT_MS = 800; // the bag's slide-out (style.css .cart-wrapper, 0.8 s): still busy while it leaves
  const TICK_MS = 1000;
  const DESKTOP = '(min-width: 1025px)'; // the product page's own desktop break (assets/sofsy-pdp.css)
  const OUT_MS = 300;
  const CLEAR_WAIT_MS = 2000; // the menu line: how long to wait for the menu to finish closing
  const SNOOZE_KEY = 'sofsy_wd_closed_at'; // localStorage: Date.now() at the x
  const VISIT_KEY = 'sofsy_wd_shown'; // sessionStorage: the drawer has opened in this visit
  const OFFERS = ['gloves', 'pct10'];
  const ENTRIES = ['menu', 'auto', 'exit', 'campaign'];
  const AUTO_ENTRIES = ['auto', 'exit', 'campaign']; // the automatic openings: seen check, yield
  const CUSTOM_SOURCE = 'Welcome drawer';
  // A size or Add to Bag tap, on the page, the pinned bar, the size menu or the size and quantity drawer.
  const INTENT =
    '[data-sofsy-track="pdp.size.tap"], [data-sofsy-track^="pdp.size_menu."], [data-sofsy-track="pdp.atb.tap"], ' +
    '[data-sofsy-track="pdp.option.change"], [data-sofsy-track^="qd."], [data-qd-pill], .sticky-button-container, ' +
    '.product-form__submit, button[name="add"], sofsy-qty-drawer, ' +
    // a product card on home, collection and search pages: its picture, name, colour swatch or quick add
    '[data-sofsy-track^="card.product."], [data-sofsy-track^="swatch."], [class*="quick-add"], [data-quick-add]';

  // What an automatic opening must never cover: Add to Bag (the page's button and the phone's pinned bar) and the
  // price (sections/main-product.liquid, snippets/pdp-name-block.liquid). Not the Indulgence Privilege table further
  // down (Ben, 5 Oct: he judges that on the preview). Only what is visible on screen counts.
  const BUY =
    '#main-product .product-form__submit, #main-product .sticky-button-container, #main-product [data-pdp-price], ' +
    '#main-product [id^="price-"], #main-product .variant-price';

  const isOffer = (value) => OFFERS.indexOf(value) > -1;
  const isAutoEntry = (entry) => AUTO_ENTRIES.indexOf(entry) > -1;
  const now = () => (window.performance && typeof window.performance.now === 'function' ? window.performance.now() : Date.now());

  /* ---------- storage, never throwing ---------- */
  const store = (kind) => {
    try {
      return window[kind] || null;
    } catch (_) {
      return null;
    }
  };
  const get = (kind, key) => {
    try {
      const s = store(kind);
      return s ? s.getItem(key) : null;
    } catch (_) {
      return null;
    }
  };
  const set = (kind, key, value) => {
    try {
      const s = store(kind);
      if (s) s.setItem(key, value);
    } catch (_) {
      /* private mode: this page view still remembers (state below) */
    }
  };

  /* ---------- pure rules (exported for the unit test) ---------- */

  // The x was tapped at closedAt (epoch ms, as stored); is the 1-day pause still on at nowEpoch?
  const isSnoozed = (closedAt, nowEpoch) => {
    const at = Number(closedAt);
    return Number.isFinite(at) && at > 0 && nowEpoch >= at && nowEpoch - at < SNOOZE_MS;
  };

  // Does this address (location.search) mark a sign-up campaign visit? utm_campaign containing "signup", "sign_up",
  // "sign-up" or "sign up" (any case), or wd=1.
  const campaignMarker = (search) => {
    try {
      if (typeof URLSearchParams !== 'function') return false;
      const q = new URLSearchParams(String(search || ''));
      if (q.get('wd') === '1') return true;
      const c = q.get('utm_campaign');
      return typeof c === 'string' && /sign[-_ ]?up/i.test(c);
    } catch (_) {
      return false;
    }
  };

  // FORCED OFFER: wd_offer in this address, when it is exactly a known offer; else null (the A/B decides).
  const FORCED_KEY = 'sofsy_wd_offer'; // sessionStorage: the forced offer for this visit (known offers only)
  const forcedOffer = (search) => {
    try {
      if (typeof URLSearchParams !== 'function') return null;
      const v = new URLSearchParams(String(search || '')).get('wd_offer');
      return isOffer(v) ? v : null;
    } catch (_) {
      return null;
    }
  };

  /* May the drawer open by itself now? null = yes; otherwise the reason it may not.
   * s: { trigger: 'time' | 'exit', kind: 'product' | 'browse' | null (PAGE_KINDS), campaign (a sign-up campaign
   *      visit: CAMPAIGN_RULES' timing on the same pages), signedUp, snoozed, shownThisVisit,
   *      designMode, elapsed (ms since the page started), scrollShare (0..1, the most she has scrolled), sinceIntent
   *      (ms since her last buying tap; Infinity if none), busy (something else is open), desktop,
   *      covers (the drawer would cover a visible price or Add to Bag; measured last, only when all else passes) }
   * Reasons that pass with time (too_early, waiting, recent_tap, busy, covers_buy) are checked again on the next
   * tick; the others end the automatic opening for this page view. */
  const autoBlock = (s) => {
    if (!Object.prototype.hasOwnProperty.call(RULES, s.kind)) return 'not_this_page';
    const rules = s.campaign ? CAMPAIGN_RULES : RULES[s.kind];
    if (s.designMode) return 'design_mode';
    if (s.signedUp) return 'signed_up';
    if (s.snoozed) return 'snoozed';
    if (s.shownThisVisit) return 'shown_this_visit';
    if (s.trigger === 'exit' && !s.desktop) return 'not_desktop';
    if (!(s.elapsed >= rules.notBefore)) return 'too_early';
    if (s.trigger !== 'exit' && !(s.elapsed >= rules.after || s.scrollShare >= rules.scroll)) return 'waiting';
    if (s.sinceIntent < QUIET_AFTER_INTENT_MS) return 'recent_tap';
    if (s.busy) return 'busy';
    if (s.covers) return 'covers_buy';
    return null;
  };
  // Two screen rectangles share any area (touching edges do not count).
  const overlaps = (a, b) => !!a && !!b && a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
  const coversAny = (drawerRect, rects) => (rects || []).some((r) => overlaps(drawerRect, r));

  const FINAL = ['not_this_page', 'design_mode', 'signed_up', 'snoozed', 'shown_this_visit'];

  // Klaviyo's signup_source stays 'drawer_menu' | 'drawer_auto' (every automatic opening is 'drawer_auto');
  // signup_entry tells exit and campaign apart ('drawer_' + entry).
  const signupSource = (entry) => 'drawer_' + (entry === 'menu' ? 'menu' : 'auto');

  /* ---------- the page ---------- */
  const root = document.querySelector('[data-welcome-drawer]');
  const els = root && {
    drawer: root.querySelector('#WelcomeDrawer'),
    form: root.querySelector('.wd__form'),
    input: root.querySelector('input[name="email"]'),
    trap: root.querySelector('input[name="sofsy_hp_x"]'),
    button: root.querySelector('.wd__btn'),
    msg: root.querySelector('.wd__msg'),
    offer: root.querySelector('.wd__offer'),
    thanks: root.querySelector('.wd__ty'),
    close: root.querySelector('[data-wd-close]'),
  };

  const state = {
    open: false,
    entry: null,
    offer: null,
    startedAt: 0,
    busy: false,
    done: false,
    settled: false,
    rect: null,
    yieldCheck: 0,
    klaviyoOpen: false,
    lastIntentAt: -Infinity,
    scrollShare: 0,
    autoOver: false,
    asked: false, // opened from the menu while busy() was true: not hidden by wd-yield
    unseen: 0, // automatic openings found covered at their centre, this page view
    seen: false, // this opening has been seen (drawer.open sent)
    retryAfter: 0, // no automatic opening before this (performance.now ms) after a covered one
    bagOpen: false,
    bagClosedAt: -Infinity,
    seenCheck: 0,
    opener: null,
    failed: {},
    campaign: false, // a sign-up campaign visit (read once below)
    forced: null, // FORCED OFFER: wd_offer from the landing address, kept for the visit (read once below)
  };
  // Read at load: a known wd_offer on this address is kept for the visit; else the visit's kept one, if known.
  try {
    const hit = forcedOffer(window.location && window.location.search);
    if (hit) set('sessionStorage', FORCED_KEY, hit);
    const kept = get('sessionStorage', FORCED_KEY);
    state.forced = hit || (isOffer(kept) ? kept : null);
  } catch (_) {
    state.forced = null;
  }
  // The visitor's offer: a forced one from this address, else the footer's A/B.
  const chooseOffer = (s) => state.forced || s.assignOffer();

  // Read at load: the marker on this address marks the visit; a later page in it finds the mark.
  try {
    const hit = campaignMarker(window.location && window.location.search);
    if (hit) set('sessionStorage', CAMPAIGN_KEY, '1');
    state.campaign = hit || get('sessionStorage', CAMPAIGN_KEY) === '1';
  } catch (_) {
    state.campaign = false;
  }

  const shared = () => {
    const s = window.SofsyFooterSignup;
    return s && typeof s.subscribe === 'function' && typeof s.assignOffer === 'function' ? s : null;
  };
  const signedUp = () => {
    const s = shared();
    try {
      return !!(s && typeof s.isSignedUp === 'function' && s.isSignedUp());
    } catch (_) {
      return false;
    }
  };
  const desktop = () => {
    try {
      return !!(window.matchMedia && window.matchMedia(DESKTOP).matches);
    } catch (_) {
      return false;
    }
  };
  const reducedMotion = () => {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (_) {
      return false;
    }
  };
  const track = (name, props) => {
    try {
      if (typeof window.sofsyTrack === 'function') window.sofsyTrack(name, props);
    } catch (_) {
      /* tracking never breaks the drawer */
    }
  };

  // Something else is open: the bag, the size and quantity drawer, the menu, a sheet, the country pop-up, a Klaviyo
  // pop-up, and on desktop the header's menus: a mega menu or submenu under the pointer (bundle.js
  // toggleHoverClasses: .parent__item.--active in .mega-menu-parent, header.--active-submenu), the account drawer
  // (.drawer.is-active / .is-visible, sections/account-drawer.liquid) and the search results (predictive-search[open]).
  // The classes are the ones those parts already set (assets/cart.js, qty-drawer.js, bundle.js, custom.js).
  // An element only counts when it is really on screen (onScreen below): the theme leaves some of these "open" while
  // hidden. bundle.js activateMobileDetailsDrawer() sets [open] on #PopupModal-productDrawer at load at <= 988 px,
  // where that modal is display:none (0 x 0); counting it kept every phone product page busy (no tab, a dead menu
  // line, no automatic opening; preview debug, 6 Oct). Sheets (modal-dialog[open]) are checked the same way.
  const BUSY =
    'cart-items.cart-wrapper:not(.close-drawer), sofsy-qty-drawer.is-open, modal-dialog[open], details-modal details[open], ' +
    '.halo-popup.is-show, .mega-menu-parent .parent__item.--active, header.--active-submenu, #drawer-account.is-active, ' +
    '#drawer-account.is-visible, predictive-search[open]';
  // Really visible: laid out (a client rect), a non-zero size, and not visibility:hidden on it.
  const onScreen = (el) => {
    try {
      if (!el || typeof el.getClientRects !== 'function' || el.getClientRects().length === 0) return false;
      const r = el.getBoundingClientRect();
      if (!r || !(r.width > 0) || !(r.height > 0)) return false;
      const cs = window.getComputedStyle(el);
      return !(cs && (cs.visibility === 'hidden' || cs.display === 'none'));
    } catch (_) {
      return false;
    }
  };
  // The Consentmo cookie banner (<csm-cookie-consent>, an app embed: a fixed host of 0 x 0 with its banner inside an
  // open shadow root, popover=manual; html.csm-lock while it locks the page). It shows to visitors in some regions,
  // over the bottom-left corner; a drawer opened under it was invisible and still used up the visit (Ads chat test,
  // 6 Oct). Showing = something inside it has a size; with no readable shadow root, the host itself on screen.
  const consentBannerShowing = () => {
    try {
      if (document.documentElement.classList.contains('csm-lock')) return true;
      const host = document.querySelector('csm-cookie-consent');
      if (!host) return false;
      const shadow = host.shadowRoot;
      if (!shadow) return onScreen(host);
      return Array.prototype.some.call(shadow.querySelectorAll('*'), onScreen);
    } catch (_) {
      return false;
    }
  };
  // The bag still on its way out: from the moment it starts closing (close-drawer added, or cart.js's
  // sofsy:bag-closed) for BAG_OUT_MS, so the drawer never slides in over a bag that is sliding away.
  // The bag has started closing: busy for BAG_OUT_MS, then look again (refresh), or wd-yield would stay on and keep
  // an open drawer invisible after the bag has gone.
  const bagStartedClosing = () => {
    state.bagOpen = false;
    state.bagClosedAt = now();
    window.setTimeout(() => soon(), BAG_OUT_MS + 50);
  };
  const bagLeaving = () => {
    try {
      const bag = document.querySelector('cart-items.cart-wrapper');
      if (bag) {
        const open = !bag.classList.contains('close-drawer');
        if (open) state.bagOpen = true;
        else if (state.bagOpen) bagStartedClosing();
      }
      return now() - state.bagClosedAt < BAG_OUT_MS;
    } catch (_) {
      return false;
    }
  };
  const busy = () => {
    try {
      if (bagLeaving()) return true; // first: it also notes the bag's open / closed changes on every check
      const html = document.documentElement;
      const body = document.body;
      const has = (el, c) => !!el && el.classList.contains(c);
      if (has(html, 'overflow-hidden') || has(html, 'sofsy-bag-lock') || has(html, 'sofsy-qd-lock')) return true;
      if (has(body, 'menu-open') || has(body, 'drawer-menu-opened') || has(body, 'overflow-hidden')) return true;
      if (body && /(^|\s)overflow-hidden-/.test(body.className || '')) return true;
      if (state.klaviyoOpen) return true;
      if (consentBannerShowing()) return true;
      return Array.prototype.some.call(document.querySelectorAll(BUSY), onScreen);
    } catch (_) {
      return true;
    }
  };

  /* ---------- showing and hiding ---------- */
  let pending = 0;
  const refresh = () => {
    if (!els) return;
    const isBusy = busy();
    // A drawer she asked for (the menu line) while something still counted as open stays visible until that clears.
    if (!isBusy) state.asked = false;
    document.documentElement.classList.toggle('wd-yield', isBusy && !(state.open && state.asked));
    const blocked = signedUp();
    document.querySelectorAll('[data-wd-open="menu"]').forEach((link) => {
      link.hidden = blocked || !shared();
      link.setAttribute('aria-expanded', state.open ? 'true' : 'false');
    });
  };
  const soon = () => {
    if (pending) return;
    pending = window.setTimeout(() => {
      pending = 0;
      refresh();
    }, 0);
  };

  // One line, always: the CSS picks the size; if the line still overflows (a font with other widths), step down.
  const fitOffer = () => {
    const line = els && els.offer;
    if (!line || !state.open) return;
    try {
      line.style.fontSize = '';
      let size = parseFloat(window.getComputedStyle(line).fontSize) || 16;
      for (let i = 0; i < 24 && line.scrollWidth > line.clientWidth + 0.5 && size > 11; i += 1) {
        size -= 0.5;
        line.style.fontSize = `${size}px`;
      }
    } catch (_) {
      /* the CSS size stands */
    }
  };

  const say = (text) => {
    if (!els.msg) return;
    els.msg.textContent = '';
    if (text) window.setTimeout(() => { els.msg.textContent = text; }, 60);
  };

  const fieldError = (text) => {
    if (els.input) {
      els.input.setAttribute('aria-invalid', 'true');
      els.input.setAttribute('aria-describedby', 'WelcomeDrawer-msg');
      try {
        els.input.focus({ preventScroll: true });
      } catch (_) {
        /* not focusable */
      }
    }
    say(text);
  };
  const clearError = () => {
    if (els.input) {
      els.input.removeAttribute('aria-invalid');
      els.input.removeAttribute('aria-describedby');
    }
    say('');
  };

  const setBusy = (on) => {
    state.busy = on;
    if (on) {
      els.form.setAttribute('aria-busy', 'true');
      if (els.button) els.button.setAttribute('aria-disabled', 'true');
    } else {
      els.form.removeAttribute('aria-busy');
      if (els.button) els.button.removeAttribute('aria-disabled');
    }
  };

  // real: Klaviyo answered 2xx. The pretend thank-you for a bot looks the same but sends no event.
  const showThanks = (real) => {
    state.done = true;
    els.drawer.classList.add('wd--done');
    if (els.thanks) {
      els.thanks.hidden = false;
      try {
        els.thanks.focus({ preventScroll: true });
      } catch (_) {
        /* not focusable */
      }
    }
    // The shared subscribe() already reported it through SofsyTrack.signupSuccess (the lead decider). Only an older
    // sofsy-track.js without that decider gets the bare event, as before (never as a lead).
    const decider = window.SofsyTrack && typeof window.SofsyTrack.signupSuccess === 'function';
    if (real && !decider) track('drawer.signup.success', { offer: state.offer, entry: state.entry });
    refresh();
  };

  const failOnce = (reason) => {
    if (state.failed[reason]) return;
    state.failed[reason] = true;
    track('drawer.signup.fail', { offer: state.offer, reason });
  };

  const open = (entry) => {
    if (!els || !els.drawer || state.open || ENTRIES.indexOf(entry) < 0) return false;
    const s = shared();
    if (!s || signedUp()) return false;
    if (!state.startedAt) {
      // The offer is the footer's, read now; once she has touched the form it stays for this page view.
      const offer = chooseOffer(s);
      if (!isOffer(offer)) return false;
      state.offer = offer;
      els.drawer.setAttribute('data-offer', offer);
    }
    els.drawer.classList.remove('wd--out'); // (an open during the slide-out: measured without it)
    state.rect = finalRect(); // where it lands, for the yield check while it is open
    state.settled = false;
    state.open = true;
    state.entry = entry;
    const active = document.activeElement;
    state.opener = entry === 'menu' ? active : null;
    state.asked = entry === 'menu' && busy();
    els.drawer.hidden = false;
    void els.drawer.offsetWidth; // restart the entrance
    els.drawer.classList.add('wd--in');
    document.documentElement.classList.add('wd-open');
    // The visit is marked once she has really seen it: at once when she asked (the menu line); for an automatic
    // opening only after the entrance, if the drawer is what shows at its own centre (checkSeen).
    window.clearTimeout(state.seenCheck);
    state.seen = false;
    if (entry === 'menu') markSeen();
    else state.seenCheck = window.setTimeout(checkSeen, SEEN_CHECK_MS);
    if (els.input && els.input.value && els.input.value.trim() !== '') markStarted(); // a restored address counts as a touch
    if (entry === 'menu') {
      try {
        els.drawer.focus({ preventScroll: true }); // asked for: focus moves in (no keyboard pops up on phones)
      } catch (_) {
        /* not focusable */
      }
    }
    refresh();
    fitOffer();
    return true;
  };

  // how: x | escape | yield; covered: an automatic opening that was never seen (nothing is sent for it).
  const close = (how, covered) => {
    if (!state.open) return false;
    if (!covered && how !== 'yield') markSeen(); // her x or Escape: she saw it
    state.open = false;
    const hadFocus = els.drawer.contains(document.activeElement);
    els.drawer.classList.remove('wd--in');
    els.drawer.classList.add('wd--out');
    document.documentElement.classList.remove('wd-open');
    window.setTimeout(() => {
      if (state.open) return;
      els.drawer.hidden = true;
      els.drawer.classList.remove('wd--out');
    }, reducedMotion() ? 0 : OUT_MS);
    if (!state.done && how !== 'yield') set('localStorage', SNOOZE_KEY, String(Date.now())); // a yield is not her x
    state.autoOver = true;
    if (state.seen) track('drawer.close', { how }); // a never-seen opening leaves no trace
    window.clearTimeout(state.seenCheck);
    refresh();
    if (hadFocus) {
      // Focus goes back to the menu button (the menu line itself is inside the closed menu), if she can see it.
      const seen = (el) => !!el && el.isConnected !== false && !el.hidden && (typeof el.getClientRects !== 'function' || el.getClientRects().length > 0);
      const toggle = document.querySelector('.header__icon--menu');
      const back = seen(toggle) ? toggle : null;
      try {
        if (back) back.focus({ preventScroll: true });
      } catch (_) {
        /* not focusable */
      }
    }
    state.opener = null;
    state.asked = false;
    return true;
  };

  const markStarted = () => {
    if (!state.startedAt) state.startedAt = now() || 1;
    markSeen(); // she is using it
  };

  // Seen: drawer.open is sent and the visit is marked, once per opening.
  const markSeen = () => {
    if (!state.open || state.seen) return;
    state.seen = true;
    window.clearTimeout(state.seenCheck);
    state.seenCheck = 0;
    set('sessionStorage', VISIT_KEY, '1');
    track('drawer.open', { entry: state.entry, offer: state.offer });
  };

  const submit = () => {
    if (state.busy || state.done) return;
    const s = shared();
    if (!s) return;
    markSeen(); // a tap on Join the list: she has seen it (drawer.open goes before any drawer.signup.fail)
    const email = els.input ? String(els.input.value || '').trim() : '';
    // Her mistakes first, so a person who is quick still sees why nothing happened.
    if (!email) {
      fieldError(els.form.dataset.msgEmpty || '');
      return failOnce('invalid_email');
    }
    if (!s.isEmail(email)) {
      fieldError(els.form.dataset.msgInvalid || '');
      return failOnce('invalid_email');
    }
    clearError();
    // Bots: a filled trap or no touch of the form at all get a pretend thank-you; nothing is sent.
    const trap = els.trap ? els.trap.value : '';
    if (trap || !state.startedAt) return showThanks(false);
    setBusy(true); // a second tap while she waits is ignored
    const entry = state.entry;
    s.subscribe({
      signup: { surface: 'drawer', entry }, // the lead decider (sofsy-track.js signupSuccess)
      email,
      offer: state.offer,
      startedAt: state.startedAt,
      trap,
      extra: {
        customSource: CUSTOM_SOURCE,
        signupSource: signupSource(entry),
        signupEntry: 'drawer_' + entry,
        offerForced: !!state.forced && state.offer === state.forced, // FORCED OFFER: Klaviyo signup_offer_forced
      },
    }).then((kind) => {
      setBusy(false);
      if (kind === 'success') showThanks(true);
      else if (kind === 'bot') showThanks(false);
      else {
        say(els.form.dataset.msgNetwork || '');
        failOnce('other');
      }
    });
  };

  /* ---------- automatic opening ---------- */
  const pageKind = () => (root && Object.prototype.hasOwnProperty.call(PAGE_KINDS, root.dataset.template) ? PAGE_KINDS[root.dataset.template] : null);

  // About 1 s after an automatic opening: is the drawer what shows at its own centre? If not (covered), it slides away
  // without sending or marking anything, and may try once more (see the top).
  const checkSeen = () => {
    state.seenCheck = 0;
    if (!state.open || !isAutoEntry(state.entry)) return;
    let seen = true; // cannot tell: counts as seen
    try {
      if (typeof document.elementFromPoint === 'function') {
        const r = els.drawer.getBoundingClientRect();
        const hit = document.elementFromPoint(r.left + r.width / 2, r.top + r.height / 2);
        seen = !!hit && els.drawer.contains(hit);
      }
    } catch (_) {
      seen = true;
    }
    if (seen || state.startedAt || state.busy || state.done) {
      markSeen();
      return;
    }
    // Covered: slide away and send nothing. One more try on this page, no sooner than RETRY_AFTER_MS; at most
    // MAX_UNSEEN_VISIT covered openings in the visit.
    state.unseen += 1;
    const visitUnseen = (Number(get('sessionStorage', UNSEEN_KEY)) || 0) + 1;
    set('sessionStorage', UNSEEN_KEY, String(visitUnseen));
    close('yield', true);
    if (state.unseen < MAX_UNSEEN_PAGE && visitUnseen < MAX_UNSEEN_VISIT) {
      state.autoOver = false;
      state.retryAfter = now() + RETRY_AFTER_MS;
      window.setTimeout(tick, TICK_MS);
    }
  };

  const autoState = (trigger) => ({
    trigger,
    kind: pageKind(),
    campaign: state.campaign,
    designMode: !!root && (root.dataset.designMode === 'true' || !!(window.Shopify && window.Shopify.designMode)),
    signedUp: signedUp(),
    snoozed: isSnoozed(get('localStorage', SNOOZE_KEY), Date.now()),
    shownThisVisit:
      get('sessionStorage', VISIT_KEY) === '1' || state.autoOver || (Number(get('sessionStorage', UNSEEN_KEY)) || 0) >= MAX_UNSEEN_VISIT,
    elapsed: now(),
    scrollShare: state.scrollShare,
    sinceIntent: now() - state.lastIntentAt,
    busy: busy() || state.open,
    desktop: desktop(),
  });

  // A visible element's rectangle on screen, or null (no size, off screen, or hidden by it or a parent).
  const shownRect = (el) => {
    try {
      const r = el.getBoundingClientRect();
      if (!r || !(r.width > 0) || !(r.height > 0)) return null;
      const vw = window.innerWidth || 0;
      const vh = window.innerHeight || 0;
      if (r.bottom <= 0 || r.right <= 0 || (vh && r.top >= vh) || (vw && r.left >= vw)) return null;
      for (let p = el; p && p.nodeType !== 9 && p !== document.documentElement; p = p.parentElement) {
        const cs = window.getComputedStyle(p);
        if (cs && (cs.display === 'none' || cs.visibility === 'hidden' || cs.opacity === '0')) return null;
      }
      return r;
    } catch (_) {
      return null;
    }
  };

  // Where the drawer will sit once open: laid out hidden, without its entrance, measured, put back.
  const finalRect = () => {
    const d = els.drawer;
    const was = { hidden: d.hidden, visibility: d.style.visibility, offer: d.getAttribute('data-offer') };
    try {
      if (!was.offer) {
        const s = shared();
        const offer = s ? chooseOffer(s) : null;
        if (isOffer(offer)) d.setAttribute('data-offer', offer);
      }
      d.style.visibility = 'hidden';
      d.hidden = false;
      return d.getBoundingClientRect();
    } catch (_) {
      return null;
    } finally {
      d.hidden = was.hidden;
      d.style.visibility = was.visibility;
    }
  };

  // Would the drawer cover a visible price or Add to Bag? Unknown counts as yes (it waits).
  const coversBuy = () => {
    try {
      const rect = finalRect();
      if (!rect || !(rect.width > 0)) return true;
      const rects = [];
      document.querySelectorAll(BUY).forEach((el) => {
        const r = shownRect(el);
        if (r) rects.push(r);
      });
      return coversAny(rect, rects);
    } catch (_) {
      return true;
    }
  };

  // An automatic opening that is open: if the price or a visible Add to Bag has come under it, it steps aside.
  const yieldIfCovering = () => {
    state.yieldCheck = 0;
    if (!state.open || !isAutoEntry(state.entry)) return;
    // Never once she is using it: she has touched the form, a send is on its way, the thank-you shows, or focus is in
    // the drawer (on iPhones the keyboard opening can scroll the page under it).
    if (state.startedAt || state.busy || state.done) return;
    try {
      if (els.drawer.contains(document.activeElement)) return;
    } catch (_) {
      /* no focus reading: the checks above stand */
    }
    try {
      let rect = state.rect;
      if (state.settled) rect = els.drawer.getBoundingClientRect(); // after the entrance, its own box is the truth
      if (!rect || !(rect.width > 0)) return;
      const rects = [];
      document.querySelectorAll(BUY).forEach((el) => {
        const r = shownRect(el);
        if (r) rects.push(r);
      });
      if (coversAny(rect, rects)) close('yield');
    } catch (_) {
      /* stays open */
    }
  };
  const soonYield = () => {
    if (state.yieldCheck || !state.open || !isAutoEntry(state.entry)) return;
    state.yieldCheck = window.setTimeout(yieldIfCovering, 200);
  };

  const tryAuto = (trigger) => {
    if (state.autoOver || state.open || now() < state.retryAfter) return;
    const s = autoState(trigger);
    let reason = autoBlock(s);
    if (reason === null) {
      s.covers = coversBuy(); // measured last, only when everything else allows it
      reason = autoBlock(s);
    }
    if (reason === null) {
      if (open(trigger === 'exit' ? 'exit' : s.campaign ? 'campaign' : 'auto')) state.autoOver = true;
    } else if (FINAL.indexOf(reason) > -1) {
      state.autoOver = true;
    }
  };

  const tick = () => {
    refresh();
    if (state.autoOver) return;
    tryAuto('time');
    if (!state.autoOver) window.setTimeout(tick, TICK_MS);
  };

  const readScroll = () => {
    try {
      const doc = document.documentElement;
      const room = Math.max(1, (doc.scrollHeight || 0) - (window.innerHeight || 0));
      const share = (window.scrollY || window.pageYOffset || 0) / room;
      if (share > state.scrollShare) state.scrollShare = Math.min(1, share);
    } catch (_) {
      /* no scroll reading: the 30 s still applies */
    }
  };

  /* ---------- the menu line: close the menu, then open ---------- */
  // She asked for it: it always opens. Only the menu's own closing is waited for (up to CLEAR_WAIT_MS), never
  // anything else that busy() may report.
  const menuOpen = () => {
    try {
      const body = document.body;
      const toggle = document.querySelector('.header__icon--menu');
      return !!(
        (toggle && toggle.classList.contains('--is-active')) ||
        (body && (body.classList.contains('menu-open') || body.classList.contains('drawer-menu-opened')))
      );
    } catch (_) {
      return false;
    }
  };
  const openFromMenu = () => {
    const toggle = document.querySelector('.header__icon--menu');
    if (toggle && toggle.classList.contains('--is-active')) toggle.click(); // bundle.js closes the menu on this tap
    const until = now() + CLEAR_WAIT_MS;
    const wait = () => {
      if (menuOpen() && now() < until) return window.setTimeout(wait, 50);
      return open('menu');
    };
    wait();
  };

  /* ---------- wiring ---------- */
  const init = () => {
    if (!els || !els.drawer || !els.form) return;

    root.addEventListener('click', (event) => {
      const t = event.target;
      if (!t || typeof t.closest !== 'function') return;
      if (t.closest('[data-wd-close]')) return close('x');
    });
    document.addEventListener('click', (event) => {
      const t = event.target;
      if (!t || typeof t.closest !== 'function') return;
      try {
        if (t.closest('[data-wd-open="menu"]')) {
          event.preventDefault();
          openFromMenu();
          return;
        }
        if (t.closest(INTENT)) state.lastIntentAt = now();
      } catch (_) {
        /* a selector the browser cannot read never breaks the page */
      }
    }, true);
    document.addEventListener('change', (event) => {
      const t = event.target;
      try {
        if (t && typeof t.closest === 'function' && t.closest(INTENT)) state.lastIntentAt = now();
      } catch (_) {
        /* ignore */
      }
    }, true);
    document.addEventListener('keydown', (event) => {
      if (state.open && (event.key === 'Escape' || event.key === 'Esc') && !busy()) close('escape');
    });

    els.form.addEventListener('focusin', markStarted);
    els.form.addEventListener('input', () => {
      markStarted();
      if (els.input && els.input.getAttribute('aria-invalid') === 'true') clearError();
    });
    els.form.addEventListener('submit', (event) => {
      event.preventDefault();
      submit();
    });

    // The bag starts closing (assets/cart.js): busy while it slides out.
    document.addEventListener('sofsy:bag-closed', bagStartedClosing);

    // Klaviyo's pop-ups: step aside while one is open. (A submit marks her signed up, in footer-signup.js.)
    window.addEventListener('klaviyoForms', (event) => {
      try {
        const d = event && event.detail;
        if (!d || typeof d !== 'object') return;
        if (d.type === 'open') state.klaviyoOpen = true;
        else if (d.type === 'close') state.klaviyoOpen = false;
        soon();
      } catch (_) {
        /* ignore */
      }
    });

    // The other layers' own classes and attributes: re-check when they change.
    try {
      const watch = new MutationObserver(soon);
      watch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      if (document.body) watch.observe(document.body, { attributes: true, attributeFilter: ['class'] });
      document.querySelectorAll('.mega-menu-parent').forEach((el) => {
        watch.observe(el, { attributes: true, attributeFilter: ['class'], subtree: true });
      });
      document.querySelectorAll('cart-items, sofsy-qty-drawer, modal-dialog, details-modal details, .halo-popup, header, #drawer-account, predictive-search').forEach((el) => {
        watch.observe(el, { attributes: true, attributeFilter: ['class', 'open'] });
      });
    } catch (_) {
      /* the tick and the timers still refresh */
    }

    let scrollCheck = 0;
    window.addEventListener('scroll', () => {
      readScroll();
      soonYield();
      // A scroll can move the price and Add to Bag out from under the drawer: check again once she pauses.
      if (!pageKind() || state.autoOver || scrollCheck) return;
      scrollCheck = window.setTimeout(() => {
        scrollCheck = 0;
        tryAuto('time');
      }, 250);
    }, { passive: true });
    window.addEventListener('resize', () => {
      fitOffer();
      if (state.open && state.settled) state.rect = els.drawer.getBoundingClientRect();
      soonYield();
    });
    els.drawer.addEventListener('animationend', () => {
      if (state.open) state.settled = true;
    });
    document.addEventListener('mouseout', (event) => {
      if (event.relatedTarget || event.toElement) return;
      if (typeof event.clientY === 'number' && event.clientY <= 0) tryAuto('exit');
    });

    // The serif loads early, so the drawer never swaps fonts in front of her.
    try {
      if (document.fonts && typeof document.fonts.load === 'function') {
        document.fonts.load("400 16px 'sofsy Chronicle Roman'").then(fitOffer, () => {});
      }
    } catch (_) {
      /* the fallback serif fits too */
    }

    readScroll();
    refresh();
    if (pageKind()) window.setTimeout(tick, TICK_MS);
  };

  window.SofsyWelcomeDrawer = Object.freeze({
    // pure rules, for tests
    autoBlock,
    overlaps,
    coversAny,
    BUY,
    BUSY,
    isSnoozed,
    signupSource,
    SNOOZE_MS,
    RULES,
    CAMPAIGN_RULES,
    PAGE_KINDS,
    campaignMarker,
    forcedOffer,
    isCampaignVisit: () => state.campaign,
    AUTO_AFTER_MS,
    AUTO_NEVER_BEFORE_MS,
    QUIET_AFTER_INTENT_MS,
    // for QA in the console and the render harness: open('menu'), close()
    open: (entry) => open(ENTRIES.indexOf(entry) > -1 ? entry : 'menu'),
    close: () => close('x'),
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
